import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineLoader } from "@/components/shared";
import { CheckCircle2, Circle, Phone, Mail, Wallet, Loader2, ShieldCheck, ExternalLink, Fingerprint } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const { refreshProfile } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [email, setEmail] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [busy, setBusy] = useState("");

  const loadStatus = async () => {
    try {
      const [{ data: cs }, { data: me }, { data: idv }] = await Promise.all([
        api.get("/payouts/connect-status"),
        api.get("/users/me"),
        api.get("/payouts/identity/status"),
      ]);
      setStatus(cs);
      setPhoneVerified(!!me.user?.phone_verified);
      setPhone(me.user?.phone || "");
      setEmailVerified(!!me.user?.email_verified);
      setEmail(me.user?.email || "");
      setIdentity(idv);
    } catch (e) {
      setStatus({ connected: false, onboarding_complete: false });
    }
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (params.get("connect") === "return") {
      toast.success("Returned from Stripe onboarding. Refreshing status…");
      loadStatus().then(() => refreshProfile());
    }
    if (params.get("identity") === "return") {
      toast.success("Returned from identity verification. Refreshing status…");
      loadStatus();
    }
    // eslint-disable-next-line
  }, [params]);

  const verifyPhone = async () => {
    setBusy("phone");
    try {
      const { data } = await api.post("/users/me/phone-verify", { phone });
      if (data.otp_sent) {
        setOtpSent(true);
        toast.success("We texted you a 6-digit code. Enter it below to verify.");
      } else if (data.phone_verified) {
        setPhoneVerified(true);
        setOtpSent(false);
        toast.success("Phone verified.");
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  const confirmOtp = async () => {
    setBusy("phone");
    try {
      const { data } = await api.post("/users/me/phone-verify", { phone, code: otpCode.trim() });
      if (data.phone_verified) {
        setPhoneVerified(true);
        setOtpSent(false);
        toast.success("Phone verified.");
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  const verifyEmail = async () => {
    setBusy("email");
    try {
      await api.post("/users/me/email-verify");
      setEmailVerified(true);
      toast.success("Email verified.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  const startConnect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.post("/payouts/connect-onboarding-link", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  const startIdentity = async () => {
    setBusy("identity");
    try {
      const { data } = await api.post("/payouts/identity/start", { origin_url: window.location.origin });
      if (data.verified) { toast.success("Identity already verified."); await loadStatus(); return; }
      if (data.url) { window.location.href = data.url; return; }
      if (data.simulated) {
        await api.post("/payouts/identity/simulate-complete");
        toast.success("Identity verified.");
        await loadStatus();
        return;
      }
      toast.info("Verification session started.");
      await loadStatus();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  if (!status) return <InlineLoader label="Loading onboarding" />;
  const stripeDone = status.onboarding_complete;

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Payout Onboarding</h1>
      <p className="mt-1 text-sm text-ff-medium">Complete these steps to receive funds. Payout onboarding is required before release.</p>

      <div className="mt-8 space-y-4">
        <Step done={phoneVerified} num={1} icon={Phone} title="Verify your phone">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label htmlFor="phone">Phone number</Label>
              <Input id="phone" data-testid="onboarding-phone-input" value={phone} disabled={phoneVerified}
                onChange={(e) => setPhone(e.target.value)} className="mt-1.5" placeholder="+1 202 555 0142" />
            </div>
            {!phoneVerified && (
              <Button onClick={verifyPhone} disabled={busy === "phone" || !phone} data-testid="onboarding-verify-phone"
                className="bg-ff-blue text-white hover:bg-ff-blue/90">
                {busy === "phone" ? <Loader2 className="h-4 w-4 animate-spin" /> : otpSent ? "Resend code" : "Verify"}
              </Button>
            )}
          </div>
          {!phoneVerified && otpSent && (
            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end" data-testid="onboarding-otp-row">
              <div className="flex-1">
                <Label htmlFor="otp">Enter the 6-digit code</Label>
                <Input id="otp" inputMode="numeric" data-testid="onboarding-otp-input" value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)} className="mt-1.5" placeholder="123456" />
              </div>
              <Button onClick={confirmOtp} disabled={busy === "phone" || otpCode.trim().length < 4} data-testid="onboarding-confirm-otp"
                className="bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
                {busy === "phone" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm code"}
              </Button>
            </div>
          )}
        </Step>

        <Step done={emailVerified} num={2} icon={Mail} title="Verify your email">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label htmlFor="email">Email address</Label>
              <Input id="email" data-testid="onboarding-email-input" value={email} disabled
                className="mt-1.5" placeholder="you@example.com" />
            </div>
            {emailVerified ? (
              <div className="inline-flex items-center gap-2 rounded-lg bg-[#ECFDF3] px-3 py-2 text-sm font-semibold text-ff-green" data-testid="email-verified-badge">
                <CheckCircle2 className="h-4 w-4" /> Verified
              </div>
            ) : (
              <Button onClick={verifyEmail} disabled={busy === "email" || !email} data-testid="onboarding-verify-email"
                className="bg-ff-blue text-white hover:bg-ff-blue/90">
                {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
              </Button>
            )}
          </div>
        </Step>

        <Step done={stripeDone} num={3} icon={Wallet} title="Connect a U.S. payout account (Stripe)">
          <p className="text-sm text-ff-dark">
            FightFund uses Stripe Connect to move funds securely. You'll complete Stripe's hosted onboarding.
            U.S. payout accounts only.
          </p>
          {stripeDone ? (
            <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#ECFDF3] px-3 py-2 text-sm font-semibold text-ff-green" data-testid="stripe-connected">
              <CheckCircle2 className="h-4 w-4" /> Stripe onboarding complete
            </div>
          ) : (
            <Button onClick={startConnect} disabled={busy === "connect"} data-testid="onboarding-connect-stripe"
              className="mt-3 bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
              {busy === "connect" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting</> : <>Start Stripe onboarding <ExternalLink className="ml-2 h-4 w-4" /></>}
            </Button>
          )}
        </Step>

        <Step done={!!identity?.verified} num={4} icon={Fingerprint} title="Verify your identity (KYC)">
          <p className="text-sm text-ff-dark">
            Before any funds are released, we verify each organizer's identity through Stripe Identity —
            a quick photo of a government ID. This is FightFund's "controlled before payout" safeguard.
          </p>
          {identity?.verified ? (
            <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#ECFDF3] px-3 py-2 text-sm font-semibold text-ff-green" data-testid="identity-verified">
              <CheckCircle2 className="h-4 w-4" /> Identity verified — payouts unlocked
            </div>
          ) : (
            <>
              {identity?.status === "processing" && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#FFF7ED] px-3 py-2 text-sm font-semibold text-ff-amber" data-testid="identity-processing">
                  <Loader2 className="h-4 w-4 animate-spin" /> Verification in progress — this can take a moment.
                </div>
              )}
              <Button onClick={startIdentity} disabled={busy === "identity"} data-testid="onboarding-verify-identity"
                className="mt-3 bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
                {busy === "identity"
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing</>
                  : <>{identity?.has_session ? "Continue identity verification" : "Start identity verification"} <ExternalLink className="ml-2 h-4 w-4" /></>}
              </Button>
              <p className="mt-2 text-xs text-ff-medium">Not required to launch a campaign — only before your first payout is released.</p>
            </>
          )}
        </Step>
      </div>

      <div className="mt-8 flex items-start gap-3 rounded-xl border border-ff-border bg-[#F3F4F6] p-5">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-ff-green" />
        <p className="text-sm text-ff-dark">
          Payouts release only after your campaign is approved, onboarding is complete, and there are no
          unresolved blocking flags or complaints. First payouts hold a minimum of 7 days after settlement.
        </p>
      </div>
    </div>
  );
}

function Step({ done, num, icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid={`onboarding-step-${num}`}>
      <div className="mb-4 flex items-center gap-3">
        {done ? <CheckCircle2 className="h-6 w-6 text-ff-green" /> : <Circle className="h-6 w-6 text-ff-medium" />}
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ff-blue text-white"><Icon className="h-4.5 w-4.5" /></span>
        <h3 className="font-heading text-lg font-bold text-ff-blue">{title}</h3>
      </div>
      {children}
    </div>
  );
}
